'use client';

import { useRef, useEffect, useState } from 'react';
import { Link } from 'src/navigation';
import SanityImage from '@sanity-image';
import Player from '@vimeo/player';
import cn from 'classnames';
import IconButton from 'components/controls/buttons/icon-button';
import SanitySource from 'components/helper/sanity-source';
import MarkdownParser from 'components/rich-text/markdown-parser';
import useMediaQuery from 'hooks/use-media-query';
import PlayIcon from 'assets/icons/play.svg';
import PauseIcon from 'assets/icons/pause.svg';
import LoadingSpinner from 'assets/icons/loading-spinner.svg';
import type { Image } from '@metmuseum/types';
import styles from './video-banner.module.scss';

export default function VideoBanner({
	vimeoID,
	title,
	cta,
	link,
	mobilePortrait,
	landscapeFallBack,
	disableVideo,
}: VideoBannerProps) {
	const { matches: isDesktop } = useMediaQuery('(min-width: 960px)');
	const { matches: reduceMotion } = useMediaQuery(
		'(prefers-reduced-motion: reduce)',
	);
	const shouldShowVideo = isDesktop && !disableVideo;

	return (
		<section
			className={cn(styles.videoBanner, {
				[styles.hasCopy]: !!title || !!(cta && link),
			})}
		>
			{(!!title || !!(cta && link)) && (
				<div className={styles.copy}>
					{!!title && (
						<h1 className={styles.title}>
							<MarkdownParser>{title}</MarkdownParser>
						</h1>
					)}
					{!!(cta && link) && (
						<Link
							href={link}
							className={cn('button', 'secondary', styles.button)}
						>
							{cta}
						</Link>
					)}
				</div>
			)}
			{shouldShowVideo && (
				<VideoPlayer
					// We want to reset the video player any time any of these props change.
					key={`${vimeoID}-${reduceMotion}`}
					vimeoID={vimeoID}
					reduceMotion={reduceMotion}
				/>
			)}
			<div className={styles.image}>
				<picture className={styles.picture}>
					{mobilePortrait && (
						<SanitySource media="(max-width: 959px)" image={mobilePortrait} />
					)}
					{landscapeFallBack && (
						<SanitySource
							media="(min-width: 960px)"
							image={landscapeFallBack}
						/>
					)}
					<SanityImage fill={true} priority={true} image={mobilePortrait} />
				</picture>
			</div>
		</section>
	);
}

function VideoPlayer({
	vimeoID,
	reduceMotion,
}: {
	vimeoID: string;
	reduceMotion: boolean;
}) {
	const [isPlaying, setIsPlaying] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isPlayerReady, setIsPlayerReady] = useState(false);
	const [hasBeenInitialized, setHasBeenInitialized] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const playerRef = useRef<Player | null>(null);
	const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!containerRef.current) return;

		// No manual cleanup required because destroy additionally removes the iframe from the DOM.
		const params = new URLSearchParams({
			background: '1',
			preload: 'auto', // Load metadata and the first few seconds of video immediately.
			autoplay: '1',
			loop: '1',
			controls: '0',
			muted: '1',
			dnt: '1',
		});

		// We're constructing our own iframe here instead of letting the Player constructor handle it because
		// there's a bug in the latter code path that causes a race condition that can sometimes error.
		const iframe = document.createElement('iframe');
		iframe.src = `https://player.vimeo.com/video/${vimeoID}?${params}`;
		iframe.allow = 'autoplay';
		iframe.role = 'presentation';
		iframe.tabIndex = -1;
		iframe.height = '100%';
		iframe.width = '100%';
		iframe.dataset.testId = 'vimeo-iframe';
		// https://www.chromatic.com/docs/ignoring-elements/
		iframe.dataset.chromatic = 'ignore';

		containerRef.current.appendChild(iframe);

		const player = new Player(iframe);
		player
			.ready()
			.then(
				() => {
					if (!playerRef.current) return;
					setIsPlayerReady(true);

					if (reduceMotion) {
						setIsLoading(false);
						return player.pause();
					}

					return player.play();
				},
				(err) => {
					console.error(`Vimeo player failed to initialize: ${err.message}`);
				},
			)
			.catch((err) => {
				console.error(`Vimeo player failed to play: ${err.message}`);
			});

		// Note that we're waiting for the 'playing' event which fires when the video
		// actually starts playing. There is also a 'play' event, but this one fires
		// when playback is initiated. Delaying it until it actually starts playing allows
		// us to avoid a flash as WebKit resizes the iframe in Safari.
		player.on('playing', () => {
			setIsLoading(false);
			setIsPlaying(true);
			setHasBeenInitialized(true);
		});
		player.on('pause', () => {
			setIsLoading(false);
			setIsPlaying(false);
			setHasBeenInitialized(true);
		});

		playerRef.current = player;

		return () => {
			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
				loadingTimeoutRef.current = null;
			}

			if (playerRef.current === player) {
				playerRef.current = null;
			}

			player.off('playing');
			player.off('pause');

			player.destroy().catch((err) => {
				console.error(`Failed to destroy Vimeo player: ${err.message}`);
			});
		};
	}, [vimeoID, reduceMotion]);

	const togglePlayPause = () => {
		if (!playerRef.current) return;

		if (loadingTimeoutRef.current) {
			clearTimeout(loadingTimeoutRef.current);
			loadingTimeoutRef.current = null;
		}

		if (isPlaying) {
			playerRef.current.pause();
		} else {
			playerRef.current.play();

			// Delay the loading state by a bit to avoid a flash
			// of the loading spinner when the video plays quickly.
			loadingTimeoutRef.current = setTimeout(async () => {
				if (await playerRef.current?.getPaused()) {
					setIsLoading(true);
				}
			}, 200);
		}
	};

	return (
		<div>
			<div
				className={cn(
					styles.vimeoContainer,
					hasBeenInitialized && styles.visible,
				)}
				ref={containerRef}
				data-testid="vimeo-container"
			/>
			<IconButton
				onClick={togglePlayPause}
				lightMode={true}
				small={true}
				aria-label={isPlaying ? 'Pause video' : 'Play video'}
				aria-hidden={!isPlayerReady}
				icon={
					isLoading ? (
						<LoadingSpinner data-testid="loading-icon" />
					) : isPlaying ? (
						<PauseIcon data-testid="pause-icon" />
					) : (
						<PlayIcon data-testid="play-icon" />
					)
				}
				className={cn(styles.playPauseButton, isPlayerReady && styles.visible)}
				data-testid="play-pause-button"
			/>
		</div>
	);
}

export interface VideoBannerProps {
	vimeoID: string;
	landscapeFallBack: Image;
	mobilePortrait?: Image;
	disableVideo?: boolean;
	cta?: string;
	link?: string;
	title?: string;
}
